import { motion, useDragControls, type PanInfo } from 'motion/react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBackHandler } from '../../backStack';
import { useBodyScrollLock } from '../useBodyScrollLock';

export interface SheetFrameProps {
  onClose: () => void;
  ariaLabelledBy: string;
  /** Registry component the sheet is inspected / themed as. */
  inspectName: string;
  /** The caller's own surface classes (fill, top radius), so each sheet keeps
   *  its literal `var(--co-<Name>-…)` knobs. */
  panelClassName: string;
  /** Classes for the drag-handle pill (its fill). */
  handleClassName: string;
  children: ReactNode;
}

/** Drag distance (px) or fling speed (px/s) past which a pull-down closes. */
const DISMISS_OFFSET_PX = 120;
const DISMISS_VELOCITY = 500;
const SLIDE_TRANSITION = { duration: 0.3, ease: [0.16, 1, 0.3, 1] } as const;

/**
 * Bottom-sheet frame (sleek's odds and shipping sheets, partner mockup): it
 * slides up from the bottom instead of the centred `Modal`. Portalled to
 * <body> so no transformed ancestor becomes the containing block of the fixed
 * layers. Closes on a backdrop tap, the host back button, or a pull-down on
 * the top band — the drag listens there only, so taps inside never start a
 * drag.
 */
export function SheetFrame({ onClose, ariaLabelledBy, inspectName, panelClassName, handleClassName, children }: SheetFrameProps) {
  useBodyScrollLock();
  useBackHandler(true, onClose, 'overlay');
  const dragControls = useDragControls();

  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > DISMISS_OFFSET_PX || info.velocity.y > DISMISS_VELOCITY) onClose();
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <motion.div
        aria-hidden
        data-co-component="Modal"
        className="absolute inset-0"
        // Darker than the centred Modal's 45%: the sheet dims the page to 80%
        // (partner Figma 2026-10-06). Sleek-only frame, so modern is unaffected.
        style={{ backgroundColor: 'var(--co-Modal-backdropFill, rgba(0,0,0,0.8))' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={SLIDE_TRANSITION}
        onClick={onClose}
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        data-co-component={inspectName}
        className={`absolute inset-x-0 bottom-0 mx-auto flex max-h-[calc(100%-var(--page-safe-top,0px)-24px)] max-w-[500px] flex-col px-6 pt-1 ${panelClassName}`}
        style={{ paddingBottom: 'calc(20px + var(--page-safe-bottom, 0px))' }}
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={SLIDE_TRANSITION}
        drag="y"
        dragListener={false}
        dragControls={dragControls}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.6 }}
        onDragEnd={onDragEnd}
      >
        {/* The grab zone is the sheet's whole top band — edge to edge, from
            the top down to where the content starts (48px) — not just the
            pill: a 64×20 target was hard to catch (partner Loom 2026-10-14).
            `-mx-6 -mt-1` cancel the panel's padding; the pill stays 12px
            from the top as before. */}
        <div
          aria-hidden
          className="-mx-6 -mt-1 flex h-12 shrink-0 cursor-grab touch-none justify-center pt-3"
          onPointerDown={(e) => dragControls.start(e)}
        >
          <span className={`h-1 w-9 rounded-full ${handleClassName}`} />
        </div>
        {/* Taller content (e.g. six odds tiers on a short phone) scrolls
            inside the sheet; the handle stays put above it. */}
        <div className="min-h-0 overflow-y-auto overscroll-contain">{children}</div>
      </motion.div>
    </div>,
    document.body,
  );
}
